import { useLogReducerContext } from '@Log/context';
import { Flex } from '@tonic-one/react';
import { DataGrid, ColumnDef } from '@tonic-one/react-data-grid';
import { forwardRef, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ColumnConfigInterface,
  LogTypeEnum,
  PageInterface,
} from '../../../types/log';
import { useDataGridInfiniteScroll } from '../../../utils/useDataGridInfiniteScroll';

interface Props {
  logType: string;
  columns: ColumnDef[];
  page: PageInterface;
  columnConfig: ColumnConfigInterface;
  logs: any[];
  total: number;
  isLoading: boolean;
  setPageStart: any;
  setColumnConfig: any;
  hasNextPage: () => boolean;
}

function LogTable(props: Props, ref: any) {
  const {
    logType,
    columns,
    page,
    columnConfig,
    logs,
    total,
    isLoading,
    setPageStart,
    setColumnConfig,
    hasNextPage,
  } = props;
  const { t } = useTranslation();
  const dataGridRef = ref;
  const {
    state: { sort },
    setSortKey,
  } = useLogReducerContext();

  const columnVisibility = useMemo(() => {
    return columnConfig.hideIds.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [columnConfig.hideIds]);

  const sorting = useMemo(
    () => ({
      columnId: sort.field,
      direction: sort.direction as 'asc' | 'desc',
    }),
    [sort]
  );

  const handleSortingChange = useCallback(
    (newSorting: { columnId: string; direction: 'asc' | 'desc' }) => {
      if (
        sorting.columnId === 'timestamp' &&
        newSorting.columnId === undefined &&
        newSorting.direction === undefined
      ) {
        setSortKey({
          field: 'timestamp',
          direction: 'asc',
        });
      } else {
        setSortKey({
          field: newSorting.columnId ?? 'timestamp',
          direction: newSorting.direction ?? 'desc',
        });
      }
    },
    [setSortKey, sorting.columnId]
  );

  const handleColumnReorder = useCallback(
    (order: string[]) => {
      setColumnConfig({
        ...columnConfig,
        order,
      });
    },
    [columnConfig, setColumnConfig]
  );

  // Infinite scroll logic
  const hasNextPageValue = useMemo(() => {
    if (!hasNextPage() && logType === LogTypeEnum.URLTRACKING) {
      return false;
    }
    if (
      page.start * page.size >= total &&
      logType !== LogTypeEnum.URLTRACKING
    ) {
      return false;
    }
    return hasNextPage();
  }, [hasNextPage, logType, page.start, page.size, total]);

  const fetchNextPage = useCallback(() => {
    if (hasNextPageValue && !isLoading) {
      setPageStart(page.start + 1);
    }
  }, [hasNextPageValue, isLoading, page.start, setPageStart]);

  useDataGridInfiniteScroll({
    dataGridRef,
    hasNextPage: hasNextPageValue,
    isFetchingNextPage: isLoading || false,
    fetchNextPage,
  });

  return (
    <Flex flex="1" direction="column">
      <DataGrid
        ref={dataGridRef}
        data={logs}
        columns={columns}
        isColumnSortable={true}
        autoSorting={false}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        isColumnResizable={true}
        isColumnReorderable={true}
        columnOrder={columnConfig.order}
        onColumnOrderChange={handleColumnReorder}
        columnVisibility={columnVisibility}
        showPagination={false}
        isLoading={isLoading}
        emptyContent={t('common.noData')}
      />
    </Flex>
  );
}

const ForwardRefLogTable = forwardRef(LogTable);

export default memo(ForwardRefLogTable);
